import {
  Card,
  CardContent,
  Stack,
  Avatar,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import { useDispatch } from "react-redux";
import { routeGroupActions } from "../../../store/slices/routeGroupSlice";
import DeleteIcon from "@mui/icons-material/Delete";
import { routeActions } from "../../../store/slices/routeSlice";
import { ClipboardCopy, CopyIcon, Trash, Users } from "lucide-react";
import {
  copyTextToClipboard,
  formatRouteGroupForClipboard,
} from "../../../helpers/formatters/formatText";
import { DeleteOutlineOutlined, PeopleOutline } from "@mui/icons-material";

const RouteGroupCard = ({
  routeGroupId,
  color,
  title,
  calculatedPassengers,
  refetch,
  fromOrganization = false,
  children,
  isNode = false,
  date = null,
  routes,
}) => {
  const dispatch = useDispatch();
  const deleteRouteFromRouteGroupHandler = (passengerId) => {
    dispatch(
      routeGroupActions.deleteRouteFromRouteGroupPut({
        routeId: passengerId,
        id: routeGroupId,
        onSucceededDeletedRouteFromRouteGroup: refetch,
      }),
    );
  };

  const deleteRouteGroupHandler = () => {
    dispatch(
      routeGroupActions.deleteRouteGroupDelete({
        id: routeGroupId,
        onSucceededDeletedRouteGroup: refetch,
      }),
    );
  };

  const copyGroupToClipboard = () => {
    dispatch(
      routeGroupActions.copyRouteGroupToClipboard({
        date: date,
        id: routeGroupId,
        onSucceededCopyRouteGroupToClipboard: (data) => {
          const formattedText = formatRouteGroupForClipboard(data);
          copyTextToClipboard(formattedText);
        },
      }),
    );
  };

  const onSentRouteHandler = (passengerId) => {
    dispatch(
      routeActions.updateRouteAsSentPut({
        payload: { passengerId },
        onSucceededSentRoute: refetch,
      }),
    );
  };

  return (
    <Card
      sx={{
        width: "100%",
        borderRadius: 3,
        boxShadow: 4,
        overflow: "hidden",
        bgcolor: "#f9fafb",
        maxWidth: isNode ? "300px" : "unset",
      }}
    >
      <Box
        sx={{
          backgroundColor: color || "black",
          width: "100%",
          color: "white",
          px: 1,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            flexGrow: 1,
          }}
        >
          {title}
        </Typography>

        <Box display="flex" alignItems="center" sx={{}}>
          {isNode && (
            <Tooltip title="Kopiraj grupu" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  copyGroupToClipboard();
                }}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  ml: 0.5,
                  mr: 0.5,
                  p: "2px",
                  color: "white",
                }}
              >
                <ClipboardCopy size={18} />
              </IconButton>
            </Tooltip>
          )}
          <Users size={18} />
          <Typography
            sx={{
              fontSize: "14px",
              marginRight: "6px !important",
              marginLeft: "2px !important",
            }}
          >
            {calculatedPassengers.length}
          </Typography>
          {!fromOrganization && (
            <Trash
              size={18}
              sx={{ cursor: "pointer" }}
              onClick={deleteRouteGroupHandler}
            />
          )}
        </Box>
      </Box>
      {!isNode && (
        <Box
          style={{
            height: "auto",
            border: `3px solid ${color}`,
            borderRadius: "0 0 12px 12px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "40px 40px 1.5fr 1.5fr 2fr 1.5fr 40px",
              alignItems: "center",
              gap: "15px",
              width: "100%",
              height: "48px",
              borderBottom: "1px solid #d4d4d4",
              paddingX: "8px",
              fontWeight: 600,
              color: "#444",
              fontSize: "14px",
            }}
          >
            <Typography sx={{ fontSize: "14px", color: "#757575" }}>
              Vreme
            </Typography>
            <Typography
              sx={{ lineHeight: "1", fontSize: "14px", color: "#757575" }}
            >
              Br. osoba
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#757575" }}>
              Ime
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#757575" }}>
              Adresa
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#757575" }}>
              Let
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#757575" }}>
              Broj telefona
            </Typography>
            <Box /> {/* prazan prostor za ikonicu kolonu */}
          </Box>

          {children}
        </Box>
      )}
      {isNode && (
        <CardContent sx={{ px: 2, py: 1 }}>
          <Stack spacing={1}>
            {routes.map((route, index) => {
              const mainPassenger = route.passengers.find(
                (p) => p.ageType === 1,
              );
              if (!mainPassenger) return null;

              const totalAdults = route.passengers.filter(
                (p) => p.ageType === 1,
              ).length;
              const totalChildren = route.passengers.filter(
                (p) => p.ageType === 2 || p.ageType === 3,
              ).length;

              return (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      className="me-2"
                      checked={mainPassenger.isSent}
                      onChange={() => onSentRouteHandler(mainPassenger.id)}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        color="text.primary"
                        sx={{
                          fontSize: "14px",
                          color: "#171717",
                          fontWeight: 600,
                        }}
                      >
                        {(totalAdults > 0 || totalChildren > 0) && (
                          <>
                            {totalAdults > 0 && totalChildren > 0
                              ? `${totalAdults}+${totalChildren} `
                              : totalAdults > 0
                                ? `${totalAdults} `
                                : totalChildren > 0
                                  ? `${totalChildren} `
                                  : ""}
                          </>
                        )}
                        {mainPassenger.fullName}
                      </Typography>

                      <Typography variant="body2" color="text.primary" sx={{}}>
                        {mainPassenger.address || "Nepoznata adresa"}
                      </Typography>
                    </Box>
                  </Box>

                  {!fromOrganization && (
                    <DisabledByDefaultIcon
                      sx={{ color: "red", cursor: "pointer" }}
                      onClick={() =>
                        deleteRouteFromRouteGroupHandler(mainPassenger.id)
                      }
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      )}
    </Card>
  );
};

export default RouteGroupCard;
